/**
 * @fileoverview Syndicator Showdown — Australian Timezone Utilities
 * @module utils/timezones
 */

export const STATE_TIMEZONES = {
    QLD:  { iana: 'Australia/Brisbane',  abbr: 'AEST',     label: 'Brisbane (QLD)',    offsetH: 10,   dst: false },
    NSW:  { iana: 'Australia/Sydney',    abbr: 'AEST/AEDT', label: 'Sydney (NSW)',      offsetH: 10,   dst: true  },
    VIC:  { iana: 'Australia/Melbourne', abbr: 'AEST/AEDT', label: 'Melbourne (VIC)',   offsetH: 10,   dst: true  },
    TAS:  { iana: 'Australia/Hobart',    abbr: 'AEST/AEDT', label: 'Hobart (TAS)',      offsetH: 10,   dst: true  },
    SA:   { iana: 'Australia/Adelaide',  abbr: 'ACST/ACDT', label: 'Adelaide (SA)',     offsetH: 9.5,  dst: true  },
    NT:   { iana: 'Australia/Darwin',    abbr: 'ACST',      label: 'Darwin (NT)',       offsetH: 9.5,  dst: false },
    WA:   { iana: 'Australia/Perth',     abbr: 'AWST',      label: 'Perth (WA)',        offsetH: 8,    dst: false },
    ACT:  { iana: 'Australia/Sydney',    abbr: 'AEST/AEDT', label: 'Canberra (ACT)',    offsetH: 10,   dst: true  },
    NZ:   { iana: 'Pacific/Auckland',    abbr: 'NZST/NZDT', label: 'Auckland (NZ)',     offsetH: 12,   dst: true  },
  };
  
  export function getAdminTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  
  export function getAdminTimezoneLabel() {
    const iana = getAdminTimezone();
    const match = Object.values(STATE_TIMEZONES).find(tz => tz.iana === iana);
    if (match) return match.label;
    const offset = -new Date().getTimezoneOffset() / 60;
    return `${iana} (UTC${offset >= 0 ? '+' : ''}${offset})`;
  }
  
  export function raceLocalToUTC(localDateTimeStr, stateCode) {
    if (!localDateTimeStr || !stateCode) return localDateTimeStr;
    const tz = STATE_TIMEZONES[stateCode];
    if (!tz) return new Date(localDateTimeStr).toISOString();
  
    const tentativeDate = new Date(localDateTimeStr);
    const formatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz.iana,
      hour: 'numeric', minute: 'numeric', second: 'numeric',
      year: 'numeric', month: 'numeric', day: 'numeric', hour12: false,
    });
  
    const parts = formatter.formatToParts(tentativeDate);
    const get = (type) => parseInt(parts.find(p => p.type === type)?.value || '0');
    const localInTargetTz = new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    const offsetMs = localInTargetTz.getTime() - tentativeDate.getTime();
    const parsed = new Date(localDateTimeStr);
    return new Date(parsed.getTime() - offsetMs).toISOString();
  }
  
  export function formatRaceTime(utcIsoString, stateCode, includeDate = false) {
    if (!utcIsoString) return '—';
    const tz = STATE_TIMEZONES[stateCode];
    const timeZone = tz?.iana || 'Australia/Brisbane';
    const date = new Date(utcIsoString);
  
    if (includeDate) {
      return date.toLocaleString('en-AU', {
        timeZone, weekday: 'short', day: 'numeric', month: 'short',
        hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
      });
    }
    return date.toLocaleTimeString('en-AU', {
      timeZone, hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
    });
  }
  
  export function getTimezoneOffsetHint(stateCode) {
    const tz = STATE_TIMEZONES[stateCode];
    if (!tz) return '';
  
    const now = new Date();
    const adminOffset = -now.getTimezoneOffset();
    const raceFormatter = new Intl.DateTimeFormat('en-AU', { timeZone: tz.iana, timeZoneName: 'shortOffset' });
    const raceParts = raceFormatter.formatToParts(now);
    const raceOffsetStr = raceParts.find(p => p.type === 'timeZoneName')?.value || '';
    const raceMatch = raceOffsetStr.match(/GMT([+-])(\d+)(?::(\d+))?/);
    const raceOffset = raceMatch ? (raceMatch[1] === '+' ? 1 : -1) * (parseInt(raceMatch[2]) * 60 + parseInt(raceMatch[3] || '0')) : null;
  
    if (raceOffset === null) return `Enter times as ${tz.label} local time (${tz.abbr})`;
    const diffMinutes = raceOffset - adminOffset;
    const diffHours = diffMinutes / 60;
  
    if (diffHours === 0) return `${tz.label} is the same timezone as you (${tz.abbr}). Enter times directly.`;
    const absHours = Math.abs(diffHours);
    return `${tz.label} is ${absHours} hour${absHours !== 1 ? 's' : ''} ${diffHours > 0 ? 'ahead of' : 'behind'} your local time. Enter jump times as ${tz.abbr} (${tz.label} local time).`;
  }
  
  export function utcToRaceLocal(utcIsoString, stateCode) {
    if (!utcIsoString) return '';
    const tz = STATE_TIMEZONES[stateCode];
    const timeZone = tz?.iana || 'Australia/Brisbane';
    const date = new Date(utcIsoString);
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(date);
    const get = (type) => parts.find(p => p.type === type)?.value || '';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour') === '24' ? '00' : get('hour')}:${get('minute')}`;
  }
  
  export function getCountdownTo(utcIsoString) {
    if (!utcIsoString) return null;
    const diffMs = new Date(utcIsoString).getTime() - Date.now();
    if (diffMs <= 0) return null;
    const totalSec = Math.floor(diffMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  // Minute-granularity countdown (no seconds) for badges that only re-render
  // on a ~30-60s tick — e.g. My Stable card countdowns and leaderboard empty
  // states. Takes a plain millisecond-remaining delta rather than an ISO
  // string so callers can normalise Firestore Timestamp/epoch fields first.
  export function formatMinuteCountdown(remainingMs) {
    if (remainingMs === null || remainingMs === undefined || remainingMs <= 0) return null;
    const totalMinutes = Math.max(1, Math.ceil(remainingMs / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    return `${minutes}m`;
  }